// Lib
import { Action, handleActions } from 'redux-actions';

// Actions
import { SetFeed } from '@actions/feed';
import { AddPost, ClearPosts, SetPost, UpdatePost } from '@actions/posts';

// Extend Array prototype for toObjectWithKey and only methods (declared elsewhere in the project)
declare global {
  interface Array<T> {
    toObjectWithKey(key: string): Record<string, T>;
    only(property: string): T[];
  }
}

interface Repost {
  _id: string;
  [key: string]: unknown;
}

interface Post {
  _id: string;
  repost?: Repost;
  users?: Record<string, User>;
  [key: string]: unknown;
}

interface User {
  [key: string]: unknown;
}

interface PostsState {
  [postId: string]: Post;
}

interface SetFeedPayload {
  posts: Post[];
  [key: string]: unknown;
}

interface AddPostPayload {
  post: Post;
  [key: string]: unknown;
}

type SetFeedAction = Action<SetFeedPayload>;
type SetPostAction = Action<Post>;
type AddPostAction = Action<AddPostPayload>;
type UpdatePostAction = Action<Post>;

const initialState: PostsState = {};

export default handleActions<
  PostsState,
  SetFeedPayload | Post | AddPostPayload | void
>(
  {
    [SetFeed.toString()]: (
      state: PostsState,
      { payload }: SetFeedAction,
    ): PostsState => ({
      ...state,
      ...[...payload.posts, ...payload.posts.only('repost')].toObjectWithKey(
        '_id',
      ),
    }),
    [SetPost.toString()]: (
      state: PostsState,
      { payload }: SetPostAction,
    ): PostsState => {
      // List of posts in which target post is used as a repost
      const reposted = Object.entries(state).filter(
        ([_, post]) => post && post.repost && post.repost._id === payload._id,
      );

      // Updates list of post with proper repost data
      const update = reposted.reduce<PostsState>((acc, [key, value]) => {
        acc[key] = { ...value, repost: payload };
        return acc;
      }, {});

      return {
        ...state,
        [payload._id]: { ...state[payload._id], ...payload },
        ...update,
      };
    },
    [AddPost.toString()]: (
      state: PostsState,
      { payload }: AddPostAction,
    ): PostsState => ({
      ...state,
      [payload.post._id]: payload.post,
    }),
    [UpdatePost.toString()]: (
      state: PostsState,
      { payload }: UpdatePostAction,
    ): PostsState => ({
      ...state,
      [payload._id]: payload,
    }),
    [ClearPosts.toString()]: (): PostsState => {
      return {};
    },
  },
  initialState,
);
